/**
 * AppSelect — single source of truth for ALL select/dropdown UI in the app.
 *
 * Three exports:
 *
 *  AppSelect        Single-value select. Auto-picks the right UX:
 *                     ≤ 2 options → CycleSelect (click-to-cycle reel, no dropdown)
 *                     ≥ 3 options → Popover + Command (searchable dropdown)
 *
 *  AppMultiSelect   Multi-value select. Popover + Command with checkboxes.
 *                   Supports badge or count display, custom option renderer.
 *
 *  CycleSelect      Exported for the few spots that need forced cycle-only UX
 *                   (FilterJoinPill AND/OR toggle). Prefer AppSelect elsewhere.
 *
 * Usage:
 *   import { AppSelect, AppMultiSelect } from './AppSelect';
 *
 *   <AppSelect … />                        // plain trigger — no box (default)
 *   <AppSelect … variant="bordered" />     // form-field box: border + bg + full width
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@clarix/ui';
import { Badge } from '@clarix/ui';
import { Checkbox } from '@clarix/ui';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@clarix/ui';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@clarix/ui';
import { MasterColorDot } from './MasterColorDot';

// ─── Inject directional slot-machine keyframes once ───────────────────────────
if (typeof document !== 'undefined') {
  const STYLE_ID = '__app-select-kf__';
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = [
      '@keyframes csOutUp{0%{transform:translateY(0);opacity:1}100%{transform:translateY(-110%);opacity:0}}',
      '@keyframes csInUp{0%{transform:translateY(110%);opacity:0}100%{transform:translateY(0);opacity:1}}',
      '@keyframes csOutDown{0%{transform:translateY(0);opacity:1}100%{transform:translateY(110%);opacity:0}}',
      '@keyframes csInDown{0%{transform:translateY(-110%);opacity:0}100%{transform:translateY(0);opacity:1}}',
    ].join('');
    document.head.appendChild(s);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Menu sizing
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The menu is sized by its CONTENT, not by the trigger: a narrow field (a
 * grid column, a chip) must never be what decides whether an option label is
 * readable. `menuWidth: 'trigger'` contributes a MIN width so a full-width form
 * field still gets a full-width menu, and the menu grows past it when a label
 * needs the room.
 *
 * Two ceilings keep that growth honest:
 *  - 28rem — so one pathological label can't stretch the panel across the screen.
 *  - `--radix-popover-content-available-width` — the space Radix measured
 *    between the trigger and the collision boundary, so a wide menu is never
 *    pushed off-screen. Falls back to the cap when the var is absent (jsdom).
 *
 * Anything still too long past both wraps (see `MENU_LABEL_CLS`) — it is never
 * clipped to an ellipsis.
 *
 * Written as ONE literal string: Tailwind extracts candidates from source TEXT,
 * so an interpolated `max-w-[min(${X},…)]` would never generate its class.
 */
const MENU_WIDTH_CLS =
  'w-auto max-w-[min(28rem,var(--radix-popover-content-available-width,28rem))]';

/** Option labels wrap onto as many lines as they need — never truncated. */
const MENU_LABEL_CLS = 'whitespace-normal break-words leading-snug';

// ─────────────────────────────────────────────────────────────────────────────
// Trigger box
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The `variant="bordered"` trigger — the full form-field box, for a select that
 * sits in a form beside `Input` / `Textarea` and must read as the same control.
 *
 * Kept as ONE constant so the box is a single decision rather than a class
 * string re-typed at every call site: `border-input` + `bg-background` are the
 * same tokens `Input` uses, and the `w-full` / `justify-between` pair is what
 * makes the chevron sit at the far edge of the field instead of hugging the
 * label.
 *
 * Written as literal strings (never interpolated) — Tailwind extracts candidates
 * from source TEXT, so a computed class name would never be generated.
 */
const BORDERED_TRIGGER_CLS = [
  'w-full justify-between gap-2 overflow-hidden',
  'rounded-md border border-input bg-background',
  'font-normal transition-colors duration-100',
  'hover:border-indigo-200 hover:bg-indigo-50/20',
  'focus:outline-none focus:ring-1 focus:ring-offset-0 focus:ring-ring/40 focus:border-ring/60',
].join(' ');

/** Open-state border tint. Only meaningful once a border-WIDTH class exists. */
const BORDERED_TRIGGER_OPEN_CLS = 'border-indigo-300';

// ─────────────────────────────────────────────────────────────────────────────
// Shared option type
// ─────────────────────────────────────────────────────────────────────────────

export interface AppSelectOption {
  value: string;
  label: string;
  /** Greyed-out and unclickable in the list. */
  disabled?: boolean;
  /** Secondary text shown below the label inside the dropdown. */
  description?: string;
  /** Extra text used for keyword search (multi-select). */
  searchableText?: string;
  /** Optional leading swatch color (#RRGGBB) — e.g. a master option's color.
   *  Renders a small dot before the label in the list and on selected badges. */
  color?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// CycleSelect — click-to-cycle, directional reel animation, no dropdown
// ─────────────────────────────────────────────────────────────────────────────

export interface CycleSelectOption<T extends string = string> {
  value: T;
  label: string;
}

export interface CycleSelectProps<T extends string = string> {
  value: T;
  options: CycleSelectOption<T>[];
  onChange: (value: T) => void;
  className?: string;
  disabled?: boolean;
  /** id on the button, so a `<label htmlFor>` can point at it. */
  id?: string;
  /** Overrides the default "<value> — click to cycle" accessible name. */
  ariaLabel?: string;
}

const HALF_MS = 80;
type AnimPhase = 'idle' | 'out' | 'in';

export function CycleSelect<T extends string = string>({
  value,
  options,
  onChange,
  className,
  disabled = false,
  id,
  ariaLabel,
}: CycleSelectProps<T>) {
  const [displayLabel, setDisplayLabel] = useState<string>(
    () => options.find((o) => o.value === value)?.label ?? value,
  );
  const [animPhase, setAnimPhase] = useState<AnimPhase>('idle');
  const [slideDir, setSlideDir] = useState<'up' | 'down'>('up');
  const isShuffling = animPhase !== 'idle';

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isShuffling) {
      setDisplayLabel(options.find((o) => o.value === value)?.label ?? value);
    }
  }, [value, options, isShuffling]);

  useEffect(() => {
    return () => { if (timerRef.current !== null) clearTimeout(timerRef.current); };
  }, []);

  const handleClick = useCallback(() => {
    if (disabled || options.length < 2 || isShuffling) return;

    const currentIndex = options.findIndex((o) => o.value === value);
    const nextIndex = (currentIndex + 1) % options.length;
    const nextOption = options[nextIndex];
    if (!nextOption) return;

    const dir: 'up' | 'down' = nextIndex > currentIndex ? 'up' : 'down';

    onChange(nextOption.value);
    setSlideDir(dir);
    setAnimPhase('out');

    timerRef.current = setTimeout(() => {
      setDisplayLabel(nextOption.label);
      setAnimPhase('in');
      timerRef.current = setTimeout(() => setAnimPhase('idle'), HALF_MS);
    }, HALF_MS);
  }, [disabled, options, value, onChange, isShuffling]);

  const animName =
    animPhase === 'out'
      ? slideDir === 'up' ? 'csOutUp' : 'csOutDown'
      : animPhase === 'in'
        ? slideDir === 'up' ? 'csInUp' : 'csInDown'
        : null;

  return (
    <button
      type="button"
      id={id}
      onClick={handleClick}
      disabled={disabled || options.length < 2}
      className={cn(
        'group inline-flex h-8 select-none items-center justify-between gap-1.5 overflow-hidden',
        'rounded-md border border-gray-200 bg-white px-2.5',
        'text-[11px] font-medium text-foreground',
        'transition-colors duration-100 active:scale-[0.96] transition-transform',
        'hover:border-indigo-200 hover:bg-indigo-50/50',
        'disabled:cursor-not-allowed disabled:opacity-50',
        isShuffling && 'border-indigo-200 bg-indigo-50/40',
        className,
      )}
      aria-label={ariaLabel ?? `${displayLabel} — click to cycle`}
      title="Click to cycle"
    >
      <span
        className="inline-block whitespace-nowrap tabular-nums"
        style={{
          animation: animName ? `${animName} ${HALF_MS}ms ease-in-out forwards` : 'none',
        }}
      >
        {displayLabel}
      </span>
      <ChevronDown
        className={cn(
          'h-3.5 w-3.5 shrink-0 opacity-50 transition-opacity group-hover:opacity-80',
          isShuffling && 'text-indigo-500 opacity-70',
        )}
        strokeWidth={1.5}
      />
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AppSelect — single-value smart select
// ─────────────────────────────────────────────────────────────────────────────

export interface AppSelectProps {
  value: string;
  options: AppSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  disabled?: boolean;
  /**
   * Force the search input on or off.
   * Default: auto — shown when options.length > 6.
   */
  searchable?: boolean;
  /**
   * 'auto'     — ≤2 → CycleSelect, ≥3 → dropdown  (default)
   * 'cycle'    — always CycleSelect
   * 'dropdown' — always dropdown
   */
  mode?: 'auto' | 'cycle' | 'dropdown';
  /** Popover side alignment. Default 'start'. */
  align?: 'start' | 'center' | 'end';
  /**
   * When true, shows an × button on the trigger when a value is selected,
   * allowing the user to clear the selection. Calls onChange('').
   */
  clearable?: boolean;
  /**
   * Trigger height variant.
   * 'md' (default) → h-9 text-sm px-3  (standard form fields)
   * 'sm'           → h-7 text-xs px-2.5 (filter rows, compact UIs)
   */
  size?: 'md' | 'sm';
  /**
   * Trigger BOX variant — whether the trigger draws a form-field frame.
   *
   * 'plain' (default) — bare trigger: no border, no background, no fixed width.
   *                     For a select that lives inside something already framed
   *                     (a table cell, a toolbar, a chip) where a second box
   *                     would double up.
   * 'bordered'        — full form-field box: full width, `border-input` border,
   *                     `bg-background`, and hover/focus states, so the control
   *                     sits level with the `Input`s around it on a form.
   *
   * Opt-in on purpose: `'plain'` is what every existing call site renders, so
   * adding this prop changes nothing until a caller asks for the box.
   *
   * Ignored in two places, both because the trigger is not this component's box
   * to draw: when `renderTrigger` is supplied (a custom trigger owns its own
   * framing), and on the cycle branch (`CycleSelect` is always a bordered pill).
   */
  variant?: 'plain' | 'bordered';
  /**
   * id on the trigger, so a real `<label htmlFor>` points at it.
   *
   * The trigger is a `role="combobox"` BUTTON, not an `<input>`, so implicit label
   * wrapping does not associate it — without one of these two props the control has
   * no accessible name and `getByLabelText` cannot find it. Pass `id` (with a
   * matching htmlFor) for a visible label, or `ariaLabel` when the label is visual
   * only.
   */
  id?: string;
  /** Accessible name when there is no visible `<label htmlFor>`. */
  ariaLabel?: string;
  /**
   * Custom renderer for the trigger button CONTENT (parity with
   * `AppMultiSelect.renderTrigger`). Receives the selected option, or `undefined`
   * when nothing matches `value`. Use it for a control that must not look like a
   * form field — e.g. the ticket-type property chips, which print
   * "Priority · Critical" inside a compact bordered pill.
   *
   * Providing it drops the default trigger box styling (pass your own via
   * `className`) and forces `mode: 'dropdown'` — a cycle trigger has no room for
   * custom content and would silently ignore this.
   */
  renderTrigger?: (selected: AppSelectOption | undefined) => ReactNode;
  /**
   * Menu width. `'trigger'` (default) starts at the trigger's width and grows to
   * fit the longest option; `'auto'` ignores the trigger entirely, for a compact
   * trigger (a chip, an icon button) whose width would crush the list. Neither
   * truncates an option: both grow to fit, capped by `MENU_WIDTH_CLS`.
   */
  menuWidth?: 'trigger' | 'auto';
  /**
   * Optional footnote rendered under a divider at the BOTTOM of the menu — the
   * "what does this actually do?" copy that belongs with the choice rather than
   * beside the trigger. Not an option row: it is never focusable or selectable.
   */
  footer?: ReactNode;
}

export function AppSelect({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No options found.',
  className,
  disabled = false,
  searchable,
  mode = 'auto',
  align = 'start',
  clearable = false,
  size = 'md',
  variant = 'plain',
  id,
  ariaLabel,
  renderTrigger,
  menuWidth = 'trigger',
  footer,
}: AppSelectProps) {
  // A custom trigger can only be honoured by the dropdown branch.
  const useCycle =
    !renderTrigger && (mode === 'cycle' || (mode === 'auto' && options.length <= 2));

  if (useCycle) {
    return (
      <CycleSelect
        value={value}
        options={options}
        onChange={onChange}
        disabled={disabled}
        className={className}
        id={id}
        ariaLabel={ariaLabel}
      />
    );
  }

  return (
    <SingleDropdown
      value={value}
      options={options}
      onChange={onChange}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyText={emptyText}
      className={className}
      disabled={disabled}
      searchable={searchable ?? options.length > 6}
      align={align}
      clearable={clearable}
      size={size}
      variant={variant}
      id={id}
      ariaLabel={ariaLabel}
      renderTrigger={renderTrigger}
      menuWidth={menuWidth}
      footer={footer}
    />
  );
}

// ── Internal single-select dropdown ──────────────────────────────────────────

interface SingleDropdownProps {
  value: string;
  options: AppSelectOption[];
  onChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  className?: string;
  disabled: boolean;
  searchable: boolean;
  align: 'start' | 'center' | 'end';
  clearable: boolean;
  size: 'md' | 'sm';
  variant: 'plain' | 'bordered';
  id?: string;
  ariaLabel?: string;
  renderTrigger?: (selected: AppSelectOption | undefined) => ReactNode;
  menuWidth: 'trigger' | 'auto';
  footer?: ReactNode;
}

function SingleDropdown({
  value,
  options,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  className,
  disabled,
  searchable,
  align,
  clearable,
  size,
  variant,
  id,
  ariaLabel,
  renderTrigger,
  menuWidth,
  footer,
}: SingleDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = useState(0);

  const handleOpenChange = useCallback((next: boolean) => {
    if (next && triggerRef.current) setTriggerWidth(triggerRef.current.offsetWidth);
    if (!next) setSearch('');
    setOpen(next);
  }, []);

  const handleSelect = useCallback(
    (v: string) => { onChange(v); setOpen(false); setSearch(''); },
    [onChange],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onChange('');
    },
    [onChange],
  );

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.description?.toLowerCase().includes(q),
    );
  }, [options, search]);

  const selectedOption = options.find((o) => o.value === value);
  const selectedLabel = selectedOption?.label;
  const showClear = clearable && !!value && !disabled;
  const isSm = size === 'sm';
  // A custom trigger brings its own framing (see `renderTrigger`), so the box is
  // suppressed there even when the caller asked for `bordered` — otherwise the
  // custom content would render inside a second, unwanted field.
  const isBordered = variant === 'bordered' && !renderTrigger;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          id={id}
          aria-label={ariaLabel}
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'inline-flex select-none items-center',
            'disabled:cursor-not-allowed disabled:opacity-50',
            // Both gated on `isBordered`: `border-indigo-300` is a border COLOR,
            // so on a plain trigger it has no border-width to tint and is dead
            // weight that only reads as if the open state were styled.
            isBordered && BORDERED_TRIGGER_CLS,
            isBordered && open && BORDERED_TRIGGER_OPEN_CLS,
            // `text-[13px]` matches the common `Input` / `Textarea`. The trigger's
            // "placeholder" is a <span>, not a native placeholder, so this class IS
            // the placeholder size — a `placeholder:text-*` rule would be a no-op.
            // Without it a form reads 13px in its text fields and 14px in the select
            // beside them.
            //
            // `sm` shrinks the CONTROL, not the type. It carried `text-xs` (10px on
            // this scale) so a compact filter row rendered its dropdown three steps
            // below the 13px everything around it uses — legible on a designer's
            // display, not on a dense table. The height stays `h-7`; only the text
            // comes back up to the shared reading size.
            isSm
              ? 'h-7 px-2.5 text-lg'
              : 'h-9 px-3 text-[13px]',
            selectedLabel ? 'text-foreground' : 'text-muted-foreground',
            className,
          )}
        >
          {renderTrigger ? renderTrigger(selectedOption) : <>
          {/* The trigger is a fixed-height field, so it stays one line — but a
              clipped selection must still be readable, hence the native title.
              The MENU has no such constraint (see `MENU_WIDTH_CLS`). */}
          <span className="truncate" title={selectedLabel}>{selectedLabel ?? placeholder}</span>
          {showClear ? (
            <div
              role="button"
              aria-label="Clear selection"
              onClick={handleClear}
              onMouseDown={(e) => e.preventDefault()}
              className="shrink-0 rounded-sm p-0.5 opacity-50 hover:opacity-80 hover:bg-accent"
            >
              {isSm
                ? <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                : <X className="h-4 w-4" strokeWidth={1.5} />}
            </div>
          ) : (
            // ONE trigger glyph for every list length. This used to swap to a
            // two-arrow `ChevronsUpDown` above 6 options, which made a long
            // dropdown (e.g. a 12-month picker) look like a different control
            // from the short ones sitting beside it on the same form. The
            // option count still drives the in-menu search box — it just no
            // longer changes the icon.
            isSm
              ? <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 opacity-50 transition-transform duration-150', open && 'rotate-180')} strokeWidth={1.5} />
              : <ChevronDown className={cn('h-4 w-4 shrink-0 opacity-50 transition-transform duration-150', open && 'rotate-180')} strokeWidth={1.5} />
          )}
          </>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('p-0', MENU_WIDTH_CLS)}
        align={align}
        sideOffset={4}
        // The trigger sets the FLOOR, not the width — see `MENU_WIDTH_CLS`.
        style={
          menuWidth === 'trigger' && triggerWidth ? { minWidth: triggerWidth } : undefined
        }
      >
        <Command shouldFilter={false}>
          {searchable && (
            <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} />
          )}
          <CommandList className="max-h-[260px]">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {filtered.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.disabled}
                  onSelect={() => !opt.disabled && handleSelect(opt.value)}
                  // cmdk owns `aria-selected` (it tracks the KEYBOARD highlight), so the
                  // chosen value needs its own hook — otherwise the check mark is the only
                  // signal and it is decorative, leaving a screen reader unable to tell
                  // which option is currently set.
                  aria-current={opt.value === value ? 'true' : undefined}
                  className={cn(
                    'flex cursor-pointer items-start gap-2 text-sm',
                    // The chosen row is TINTED, not just check-marked: a check alone is
                    // easy to miss in a long list. `--row-selected` is the same token
                    // the DataTable paints selected rows with, so "this one is picked"
                    // reads identically in a menu and in a table.
                    //
                    // `!` is load-bearing. `CommandItem` ships
                    // `data-[selected='true']:bg-gray-100`, and cmdk stamps
                    // `data-selected` on whatever the pointer/keyboard is over — an
                    // attribute selector, so it outranks a plain `bg-*` class and
                    // repainted the chosen row grey the instant you hovered it.
                    opt.value === value && '!bg-[rgb(var(--row-selected))] !text-primary font-medium',
                  )}
                >
                  {/* Same swatch the multi-select and the status/priority pills use, so a
                      master option reads identically wherever it is listed (FR-MAS). */}
                  {opt.color !== undefined && <MasterColorDot color={opt.color} className="mt-1.5" />}
                  <div className="flex min-w-0 flex-1 flex-col">
                    {/* Wraps, never truncates: the menu is already sized to its
                        content, so a label that still doesn't fit is a long name
                        the user needs to READ — an ellipsis there is what made
                        "Dearness Allowance" and "Conveyance Allowance" both read
                        as "…". */}
                    <span className={cn('font-medium', MENU_LABEL_CLS)}>{opt.label}</span>
                    {opt.description && (
                      <span className={cn('text-[11px] text-muted-foreground', MENU_LABEL_CLS)}>
                        {opt.description}
                      </span>
                    )}
                  </div>
                  {opt.value === value && (
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-indigo-500" strokeWidth={1.5} />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {footer && (
            <div className="border-t border-border px-3 py-2 text-[11px] leading-relaxed text-muted-foreground [&>*+*]:mt-1.5">
              {footer}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AppMultiSelect — multi-value select with checkboxes
// ─────────────────────────────────────────────────────────────────────────────

export interface AppMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: AppSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  disabled?: boolean;
  maxHeight?: string;
  /** 'badge' shows individual chips. 'count' shows "N selected". Default: 'count'. */
  displayMode?: 'badge' | 'count';
  maxBadges?: number;
  /** Custom renderer for each option row inside the dropdown. */
  renderOption?: (option: AppSelectOption, isSelected: boolean) => ReactNode;
  /** Custom renderer for the trigger button content. */
  renderTrigger?: (selected: AppSelectOption[]) => ReactNode;
}

export function AppMultiSelect({
  value,
  onChange,
  options,
  placeholder = 'Select items…',
  searchPlaceholder = 'Search…',
  emptyText = 'No items found.',
  className,
  disabled = false,
  maxHeight = '300px',
  displayMode = 'count',
  maxBadges = 3,
  renderOption,
  renderTrigger,
}: AppMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [triggerWidth, setTriggerWidth] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open && triggerRef.current) setTriggerWidth(triggerRef.current.offsetWidth);
  }, [open]);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.searchableText?.toLowerCase().includes(q) ||
        o.description?.toLowerCase().includes(q),
    );
  }, [options, search]);

  const selectedOptions = useMemo(
    () => options.filter((o) => value.includes(o.value)),
    [options, value],
  );

  const toggle = useCallback(
    (v: string) => {
      onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
      setSearch('');
    },
    [value, onChange],
  );

  const clearAll = useCallback(
    (e: React.MouseEvent) => { e.stopPropagation(); onChange([]); },
    [onChange],
  );

  const triggerContent = (): ReactNode => {
    if (renderTrigger) return renderTrigger(selectedOptions);
    if (selectedOptions.length === 0) return <span className="text-muted-foreground">{placeholder}</span>;

    if (displayMode === 'badge') {
      const visible = selectedOptions.slice(0, maxBadges);
      const remaining = selectedOptions.length - maxBadges;
      return (
        <div className="flex flex-wrap items-center gap-1">
          {visible.map((o) => (
            <Badge key={o.value} variant="secondary" className="gap-1 text-xs">
              {o.color !== undefined && <MasterColorDot color={o.color} />}
              {o.label}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggle(o.value); }}
                className="ml-1 rounded-full hover:bg-muted"
              >
                <X className="h-3 w-3" strokeWidth={1.5} />
              </button>
            </Badge>
          ))}
          {remaining > 0 && (
            <Badge variant="secondary" className="text-xs">+{remaining} more</Badge>
          )}
        </div>
      );
    }

    return `${selectedOptions.length} item${selectedOptions.length > 1 ? 's' : ''} selected`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'inline-flex min-h-7 w-full select-none items-center justify-between gap-2 overflow-hidden',
            'rounded-md border border-input bg-background px-3 py-2',
            // 13px — same rationale as the single-value trigger above: this class is
            // the placeholder size, and it keeps the control level with `Input`.
            'text-[13px] font-normal',
            'transition-colors duration-100',
            'hover:border-indigo-200',
            'focus:outline-none focus:ring-1 focus:ring-offset-0 focus:ring-ring/40 focus:border-ring/60',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <span className="flex-1 truncate text-left">{triggerContent()}</span>
          <div className="flex shrink-0 items-center gap-1">
            {selectedOptions.length > 0 && !disabled ? (
              <div
                role="button"
                aria-label="Clear selection"
                onClick={clearAll}
                onMouseDown={(e) => e.preventDefault()}
                className="cursor-pointer rounded-sm opacity-50 hover:opacity-80 hover:bg-accent"
              >
                <X className="h-3 w-3" strokeWidth={1.5} />
              </div>
            ) : (
              <ChevronDown className="h-3.5 w-3.5 opacity-50" strokeWidth={1.5} />
            )}
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('p-0', MENU_WIDTH_CLS)}
        align="start"
        // Same contract as the single-select menu: trigger width is the floor.
        style={triggerWidth ? { minWidth: triggerWidth } : undefined}
      >
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} />
          <CommandList className="max-h-full">
            <div className="overflow-x-hidden overflow-y-auto" style={{ maxHeight }} onWheel={(e) => e.stopPropagation()}>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {filtered.map((opt) => {
                  const isSelected = value.includes(opt.value);
                  const isDisabled = opt.disabled ?? false;
                  return (
                    <CommandItem
                      key={opt.value}
                      onMouseDown={(e) => e.preventDefault()}
                      onSelect={() => !isDisabled && toggle(opt.value)}
                      disabled={isDisabled}
                      className="cursor-pointer"
                    >
                      <Checkbox checked={isSelected} className="mr-2" disabled={isDisabled} />
                      {renderOption ? renderOption(opt, isSelected) : (
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          {opt.color !== undefined && <MasterColorDot color={opt.color} />}
                          <span className={cn('min-w-0 font-medium', MENU_LABEL_CLS)}>
                            {opt.label}
                          </span>
                          {opt.searchableText && (
                            <span className={cn('text-xs text-muted-foreground', MENU_LABEL_CLS)}>
                              {opt.searchableText}
                            </span>
                          )}
                        </div>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </div>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
